import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import CustomerCenterManagementEditModal from './CustomerCenterManagementEditModal';
import CustomerImageConfigModal from './CustomerImageConfigModal';
import {
  CSM_STATE,
  CSM_STATE_FILTER_OPTIONS,
  CSM_TYPE_FILTER_OPTIONS,
  buildCustomerServiceManagementPageParams,
  formatCopyStatus,
  formatCustomerServiceManagementState,
  formatCustomerServiceManagementType,
  formatDisplayType,
} from './customerCenterManagementConstants';

const EMPTY_FILTERS = {
  state: '',
  type: '',
  name: '',
};

const TABLE_COLUMNS = [
  '客服名稱（英語）',
  '客服圖標',
  '所屬客服種類（英語）',
  '類別',
  '顯示類型',
  '顯示內容',
  '狀態',
  '前台是否允許複製顯示內容',
  '順序',
  '操作',
];

export default function CustomerCenterManagementPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [imageConfigOpen, setImageConfigOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [enableTarget, setEnableTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.customerServiceManagementPageQuery(
        buildCustomerServiceManagementPageParams(pageNo, nextFilters)
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.deleteCustomerServiceManagement(deleteTarget.id);
      setMessage('刪除成功');
      setDeleteTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmDisable() {
    if (!disableTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.updateCustomerServiceManagementState({ id: disableTarget.id, state: CSM_STATE.OFF });
      setMessage('已禁用');
      setDisableTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmEnable() {
    if (!enableTarget?.id) {
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.updateCustomerServiceManagementState({ id: enableTarget.id, state: CSM_STATE.ON });
      setMessage('已啟用');
      setEnableTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>客服管理</h2>
        <div className="section-header__actions section-header__actions--wrap">
          <button type="button" className="btn btn-secondary" onClick={() => setImageConfigOpen(true)}>
            客服中心圖片配置
          </button>
          <button type="button" className="btn btn-primary" onClick={() => { setEditId(null); setEditOpen(true); }}>
            新增
          </button>
        </div>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="csmFilterState">客服狀態</label>
          <select
            id="csmFilterState"
            value={filters.state}
            onChange={(e) => updateFilter('state', e.target.value)}
          >
            {CSM_STATE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="csmFilterType">客服種類類型</label>
          <select
            id="csmFilterType"
            value={filters.type}
            onChange={(e) => updateFilter('type', e.target.value)}
          >
            {CSM_TYPE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="csmFilterName">客服名稱</label>
          <input
            id="csmFilterName"
            type="text"
            value={filters.name}
            onChange={(e) => updateFilter('name', e.target.value)}
            placeholder="請輸入客服名稱"
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap table-wrap--wide">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td className="cell-wrap">{row.enName || '未設定'}</td>
                  <td>
                    {row.image ? (
                      <img src={resolveAdminMediaUrl(row.image)} alt="" className="carousel-list-thumb" />
                    ) : (
                      '未設定'
                    )}
                  </td>
                  <td className="cell-wrap">{row.enTypeName || '未設定'}</td>
                  <td>{formatCustomerServiceManagementType(row.type)}</td>
                  <td className="cell-wrap">{formatDisplayType(row)}</td>
                  <td className="cell-wrap">{row.body || '—'}</td>
                  <td>
                    <span
                      className={
                        row.state === CSM_STATE.ON
                          ? 'status-text status-text--success'
                          : 'status-text status-text--danger'
                      }
                    >
                      {formatCustomerServiceManagementState(row.state)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={
                        row.copyStatus === 1
                          ? 'status-text status-text--success'
                          : 'status-text status-text--danger'
                      }
                    >
                      {formatCopyStatus(row.copyStatus)}
                    </span>
                  </td>
                  <td>{row.sort ?? '—'}</td>
                  <td>
                    <div className="table-actions table-actions--news">
                      <div className="table-actions__row">
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => { setEditId(row.id); setEditOpen(true); }}
                        >
                          修改
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-outline btn-sm"
                          onClick={() => setDeleteTarget(row)}
                        >
                          刪除
                        </button>
                      </div>
                      <div className="table-actions__row">
                        {row.state === CSM_STATE.ON ? (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setDisableTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--danger" aria-hidden="true">
                              ✕
                            </span>
                            禁用
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setEnableTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--success" aria-hidden="true">
                              ✓
                            </span>
                            啟用
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, filters)} />

      <CustomerCenterManagementEditModal
        open={editOpen}
        managementId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={(wasEdit) => {
          setMessage(wasEdit ? '修改成功' : '新增成功');
          loadRecords(page, filters);
        }}
      />

      <CustomerImageConfigModal
        open={imageConfigOpen}
        onClose={() => setImageConfigOpen(false)}
        onSaved={() => {
          setMessage('客服中心圖片配置已更新');
          setImageConfigOpen(false);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除客服"
        message="確定刪除此客服？"
        confirmLabel={actionLoading ? '刪除中…' : '刪除'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(disableTarget)}
        title="禁用客服"
        message="確定禁用此客服？"
        confirmLabel={actionLoading ? '處理中…' : '禁用'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDisable}
        onCancel={() => setDisableTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(enableTarget)}
        title="啟用客服"
        message="確定啟用此客服？"
        confirmLabel={actionLoading ? '處理中…' : '啟用'}
        confirmDisabled={actionLoading}
        onConfirm={confirmEnable}
        onCancel={() => setEnableTarget(null)}
      />
    </div>
  );
}
